/**
 * Typed fetch wrapper for the Sales App API.
 *
 * Session is an HttpOnly cookie (`access_token` / `refresh_token`, set by
 * apps/api/app/api/v1/auth.py) — this client never reads or stores the
 * token itself. Every request carries credentials so the cookie rides
 * along; every mutation carries an Origin header for the API's CSRF guard.
 * A 401 triggers exactly one refresh attempt (`POST /auth/refresh`) before
 * giving up. AuthProvider then tears down the authenticated UI and sends
 * the caller to /login so confidential records cannot stay on screen.
 *
 * Base URL: in the production/Docker build, apps/web's own nginx proxies
 * `/api/` to the API container same-origin (nginx.conf.template) — so the
 * default here is the relative `/api` path, not a cross-origin host, to
 * match that. `VITE_API_BASE_URL` overrides it for local `vite dev`
 * against the API's own port (8040 per platform.json) when not running
 * behind the nginx proxy.
 */

import { deviceHeaders } from "./device";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

function pageHeaders(): Record<string, string> {
  if (typeof window === "undefined") return {};
  const page = `${window.location.pathname}${window.location.search}`.slice(0, 200);
  if (!page.startsWith("/")) return {};
  return { "X-Saleslox-Page": page };
}

/** Broadcast when the API removed markup from text the user submitted (see SanitizedNoticeMiddleware). */
export const CONTENT_SANITIZED_EVENT = "sales:content-sanitized";

function announceSanitized(res: Response): void {
  const header = res.headers.get("X-Content-Sanitized");
  if (!header || typeof window === "undefined") return;
  const fields = header.split(",").map((name) => name.trim().replace(/_/g, " ")).filter(Boolean);
  if (fields.length > 0) window.dispatchEvent(new CustomEvent(CONTENT_SANITIZED_EVENT, { detail: { fields } }));
}

/** Broadcast when a request cannot be authenticated after one refresh attempt. */
export const SESSION_EXPIRED_EVENT = "sales:session-expired";

export class ApiError extends Error {
  status: number;
  body: unknown;
  code: string | null;

  constructor(status: number, message: string, body: unknown, code: string | null = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.code = code;
  }
}

export function notifySessionExpired(code?: string | null): void {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { code: code ?? null } }));
}

function looksTechnical(message: string): boolean {
  const lower = message.toLowerCase();
  if (message.length > 180) return true;
  if (lower.includes("traceback") || lower.includes("<html") || lower.includes("<!doctype")) return true;
  if (/file ".*", line \d+/i.test(message)) return true;
  const trimmed = message.trim();
  if ((trimmed.startsWith("{") || trimmed.startsWith("[")) && trimmed.length > 40) return true;
  return false;
}

function errorMessage(payload: unknown, fallback: string): string {
  const body = payload as { detail?: unknown; message?: string; fields?: unknown } | null;
  if (!body) return fallback;
  // Validation failures carry curated, field-level sentences from the API; show them in preference
  // to the generic "some of the information is not valid".
  if (Array.isArray(body.fields)) {
    const sentences = body.fields
      .map((item) => (item && typeof (item as { message?: unknown }).message === "string" ? (item as { message: string }).message : ""))
      .filter(Boolean);
    if (sentences.length > 0) return sentences.join(" ");
  }
  let raw = "";
  if (typeof body.detail === "string" && body.detail) raw = body.detail;
  else if (Array.isArray(body.detail) && body.detail.length > 0) {
    const first = body.detail[0] as { msg?: unknown; loc?: unknown } | null;
    if (first && typeof first.msg === "string" && first.msg) {
      const msg = first.msg.replace(/^Value error, /, "");
      // Name the offending field so "Input should be greater than 0" is actionable.
      const field = Array.isArray(first.loc)
        ? first.loc.filter((part) => typeof part === "string" && part !== "body").slice(-1)[0]
        : undefined;
      raw = field && /^Input should/.test(msg) ? `${String(field).replace(/_/g, " ")}: ${msg}` : msg;
    }
  } else if (body.detail && typeof body.detail === "object" && "message" in body.detail) {
    const nested = (body.detail as { message?: unknown }).message;
    if (typeof nested === "string" && nested) raw = nested;
  } else if (body.message) {
    raw = body.message;
  }
  if (!raw) return fallback;
  if (looksTechnical(raw)) return fallback;
  return raw;
}

function errorCode(payload: unknown): string | null {
  const body = payload as { detail?: unknown; code?: unknown } | null;
  if (body?.detail && typeof body.detail === "object" && body.detail && "code" in body.detail) {
    const nested = (body.detail as { code?: unknown }).code;
    return typeof nested === "string" ? nested : null;
  }
  return typeof body?.code === "string" ? body.code : null;
}

function shouldRefreshOnUnauthorized(path: string): boolean {
  return (
    path !== "/auth/login"
    && path !== "/auth/refresh"
    && path !== "/auth/password-setup"
    && path !== "/auth/password-setup/resend"
    && path !== "/auth/password-reset"
    && !path.startsWith("/public/")
  );
}

function shouldBroadcastExpiry(path: string): boolean {
  return path !== "/auth/session" && path !== "/auth/logout" && shouldRefreshOnUnauthorized(path);
}

let refreshInFlight: Promise<{ ok: true } | { ok: false; code: string | null }> | null = null;
let authGeneration = 0;
let refreshHeld = 0;

/**
 * Ignore refresh attempts that overlap a sign-in.
 *
 * Login revokes the previous session and writes a new cookie. A request that
 * was already in flight still sees the old access token, gets a 401, and would
 * otherwise POST /auth/refresh with the cookie login just replaced — one
 * transient 401 on auth/refresh immediately after sign-in.
 */
export function holdSessionRefresh(): () => void {
  authGeneration += 1;
  refreshHeld += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    refreshHeld = Math.max(0, refreshHeld - 1);
    authGeneration += 1;
  };
}

async function tryRefresh(): Promise<{ ok: true } | { ok: false; code: string | null }> {
  if (!refreshInFlight) {
    refreshInFlight = fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { Origin: window.location.origin, ...deviceHeaders(), ...pageHeaders() },
    })
      .then(async (res) => {
        if (res.ok) return { ok: true as const };
        const contentType = res.headers.get("content-type") ?? "";
        const payload: unknown = contentType.includes("application/json")
          ? await res.json().catch(() => null)
          : null;
        return { ok: false as const, code: errorCode(payload) };
      })
      .catch(() => ({ ok: false as const, code: null }))
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

function clientFallback(status: number, statusText: string): string {
  if (status >= 500) return "Something went wrong. Try again.";
  return statusText || "Request failed";
}

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  extraHeaders?: Record<string, string>;
}

/**
 * Identical mutations (same method, path and body) that are still in flight share one request.
 * A double click, a retry-click on a slow server or a re-submitted form therefore reaches the
 * API once; every caller gets the same result. Sequential repeats (after the first settles) are
 * unaffected, so deliberately adding the same thing twice still works.
 */
const mutationsInFlight = new Map<string, Promise<unknown>>();

function request<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
  extraOkStatuses: number[] = [],
): Promise<T> {
  const method = options.method ?? "GET";
  if (!MUTATING.has(method) || isRetry) return send<T>(path, options, isRetry, extraOkStatuses);
  const key = `${method} ${path} ${options.body === undefined ? "" : JSON.stringify(options.body)}`;
  const existing = mutationsInFlight.get(key);
  if (existing) return existing as Promise<T>;
  const started = send<T>(path, options, isRetry, extraOkStatuses);
  const tracked = started.finally(() => {
    if (mutationsInFlight.get(key) === tracked) mutationsInFlight.delete(key);
  });
  mutationsInFlight.set(key, tracked);
  return tracked;
}

async function send<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
  extraOkStatuses: number[] = [],
): Promise<T> {
  const method = options.method ?? "GET";
  const generation = authGeneration;
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...deviceHeaders(),
    ...pageHeaders(),
    ...options.extraHeaders,
  };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING.has(method)) headers["Origin"] = window.location.origin;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: "include",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  const contentType = res.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json") ? await res.json().catch(() => null) : null;
  const code = errorCode(payload);

  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    if (generation !== authGeneration || refreshHeld > 0) {
      throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
    }
    if (code !== "session_replaced" && code !== "session_idle") {
      const refreshed = await tryRefresh();
      if (refreshed.ok) return send<T>(path, options, true, extraOkStatuses);
      if (shouldBroadcastExpiry(path)) notifySessionExpired(refreshed.code ?? code);
      throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, refreshed.code ?? code);
    }
    if (shouldBroadcastExpiry(path)) notifySessionExpired(code);
    throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
  }

  if (!res.ok && !extraOkStatuses.includes(res.status)) {
    throw new ApiError(res.status, errorMessage(payload, clientFallback(res.status, res.statusText)), payload, code);
  }

  if (res.ok) announceSanitized(res);
  return payload as T;
}

async function uploadForm<T>(path: string, file: File, isRetry = false): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Origin: window.location.origin,
    ...deviceHeaders(),
    ...pageHeaders(),
  };
  const body = new FormData();
  body.append("file", file);
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers,
    body,
  });
  const contentType = res.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json") ? await res.json().catch(() => null) : null;
  const code = errorCode(payload);
  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    if (code !== "session_replaced" && code !== "session_idle") {
      const refreshed = await tryRefresh();
      if (refreshed.ok) return uploadForm<T>(path, file, true);
      if (shouldBroadcastExpiry(path)) notifySessionExpired(refreshed.code ?? code);
      throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, refreshed.code ?? code);
    }
    if (shouldBroadcastExpiry(path)) notifySessionExpired(code);
    throw new ApiError(401, errorMessage(payload, "Not authenticated"), payload, code);
  }
  if (!res.ok) {
    throw new ApiError(res.status, errorMessage(payload, clientFallback(res.status, res.statusText)), payload, code);
  }
  return payload as T;
}

async function downloadFile(path: string, fallbackName: string, isRetry = false): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "*/*", ...deviceHeaders(), ...pageHeaders() },
  });
  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    const refreshed = await tryRefresh();
    if (refreshed.ok) return downloadFile(path, fallbackName, true);
    if (shouldBroadcastExpiry(path)) notifySessionExpired(refreshed.code);
    throw new ApiError(401, "Not authenticated", null, refreshed.code);
  }
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, null);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const matched = /filename="([^"]+)"/.exec(disposition);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = matched?.[1] ?? fallbackName;
  link.click();
  URL.revokeObjectURL(link.href);
}

async function getBlob(path: string, isRetry = false): Promise<Blob> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "*/*", ...deviceHeaders(), ...pageHeaders() },
  });
  if (res.status === 401 && !isRetry && shouldRefreshOnUnauthorized(path)) {
    const refreshed = await tryRefresh();
    if (refreshed.ok) return getBlob(path, true);
    if (shouldBroadcastExpiry(path)) notifySessionExpired(refreshed.code);
    throw new ApiError(401, "Not authenticated", null, refreshed.code);
  }
  if (res.status === 204) return new Blob();
  if (!res.ok) {
    throw new ApiError(res.status, res.statusText, null);
  }
  return res.blob();
}

/** What private-file endpoints return: a short-lived presigned URL, not the bytes. */
export interface DownloadLink {
  url: string;
  filename: string;
  content_type?: string | null;
  expires_in: number;
}

/** Ask the API (which authorises us) for a link, then let the browser fetch the file
 *  straight from storage. The URL carries `attachment` disposition, so navigating to it
 *  saves the file; it also expires within about a minute, so it is used immediately. */
async function downloadViaLink(path: string, fallbackName: string): Promise<void> {
  const link = await request<DownloadLink>(path, { method: "GET" });
  const anchor = document.createElement("a");
  anchor.href = link.url;
  anchor.download = link.filename || fallbackName;
  anchor.rel = "noopener";
  anchor.click();
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { method: "GET", signal }),
  post: <T>(path: string, body?: unknown, extraHeaders?: Record<string, string>) =>
    request<T>(path, { method: "POST", body, extraHeaders }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  /**
   * POST that also accepts a non-2xx status as a valid, typed response
   * instead of throwing — needed for /auth/login's 300 "choose_organization"
   * leg.
   */
  postAllowStatus: <T>(path: string, body: unknown, extraOkStatuses: number[]) =>
    request<T>(path, { method: "POST", body }, false, extraOkStatuses),
  upload: <T>(path: string, file: File) => uploadForm<T>(path, file),
  download: (path: string, fallbackName: string) => downloadFile(path, fallbackName),
  getBlob: (path: string) => getBlob(path),
  downloadViaLink: (path: string, fallbackName: string) => downloadViaLink(path, fallbackName),
  /** For previews: open `link.url` in a new tab. Navigation needs no CORS or CSP allowance. */
  getLink: (path: string) => request<DownloadLink>(path, { method: "GET" }),
};
